import { useQuery } from "@tanstack/react-query";
import { Building2, MailQuestion, Search, SlidersHorizontal, Users } from "lucide-react";
import * as React from "react";

import type { InsightsGrowthLedger, InsightsGrowthLedgerRow, InsightsPeriod } from "@/api/client";
import { getInsightsGrowthLedger } from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { PeriodToggle } from "@/components/insights/PeriodToggle";
import { StatCard } from "@/components/StatCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { SortableHeader } from "@/components/ui/sortable-header";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FormError } from "@/components/ui/form-error";
import { CHANNEL_PARTNER } from "@/lib/role-labels";
import { type SortValue } from "@/lib/tableSort";
import { useTableSort } from "@/lib/useTableSort";

/** Status filter for the growth ledger. "all" passes no status param; the rest
 * map onto the display-status the backend filters by ("registered" → onboarded
 * active users; "invited"/"pending" match the display status directly). */
type GrowthStatusFilter = "all" | "invited" | "registered" | "pending";

const STATUS_TABS: { value: GrowthStatusFilter; label: string }[] = [
  { value: "invited", label: "Invited" },
  { value: "registered", label: "Registered" },
  { value: "pending", label: "Pending" },
  { value: "all", label: "All" },
];

/** CHP-848: entity-type filter — orgs are the Channel Partners, individuals
 * the Program members. Client-side over the fetched rows (the backend has no
 * type param; the row already carries `type`). */
type GrowthTypeFilter = "all" | "org" | "individual";

const TYPE_TABS: { value: GrowthTypeFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "org", label: `${CHANNEL_PARTNER}s` },
  { value: "individual", label: "Program members" },
];

/** CHP-848: the period control moves into the shared "Advanced Filters"
 * box affordance (mirrors DealAdvancedFilters: outline toggle + count badge +
 * click-outside-closing panel), sitting right of the search bar per the
 * review ask. The badge counts a non-default period (default: This Month). */
function GrowthAdvancedFilters({
  period,
  onPeriodChange,
}: {
  period: Exclude<InsightsPeriod, "all">;
  onPeriodChange: (next: Exclude<InsightsPeriod, "all">) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const count = period === "month" ? 0 : 1;

  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <Button
        type="button"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="growth-filters-toggle"
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        Advanced Filters
        {count > 0 ? (
          <span
            className="ml-1 rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground tabular-nums"
            data-testid="growth-filters-count"
          >
            {count}
          </span>
        ) : null}
      </Button>
      {open ? (
        <div
          className="absolute right-0 z-50 mt-2 w-max rounded-md border bg-popover p-4 shadow-md"
          data-testid="growth-filters-panel"
        >
          <div className="space-y-2">
            <Label>Created</Label>
            <PeriodToggle value={period} onChange={onPeriodChange} testId="growth-period" />
          </div>
        </div>
      ) : null}
    </div>
  );
}

const SEARCH_DEBOUNCE_MS = 300;

export interface InsightsGrowthDrawerProps {
  /** Open when true; false closes the drawer. */
  open: boolean;
  /** Fired when the drawer requests to close (overlay click / Esc / X). */
  onClose: () => void;
}

/** Format an ISO timestamp as a plain en-AU date, or "—" when absent. */
function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-AU");
}

/** Sortable columns for the growth ledger (CHP-417 idiom). */
type GrowthSortColumn = "entity" | "partner" | "type" | "invite" | "status";

function growthSortValue(r: InsightsGrowthLedgerRow, column: GrowthSortColumn): SortValue {
  switch (column) {
    case "entity":
      return r.entity_name;
    case "partner":
      return r.partner_name;
    case "type":
      return r.type;
    case "invite":
      return r.invite_date ? new Date(r.invite_date) : null;
    case "status":
      return r.status;
  }
}

/** Program-Wide Growth drawer (Wave 2). The Insights page's Growth-Pulse hero
 * line opens this 65vw drawer onto GET /insights/growth-ledger: three
 * segmentation summary cards (new orgs 30d, new individuals 30d, invited-not-
 * registered) over a {Entity, Partner, Type, Invite Date, Status} ledger with a
 * status filter and a dual search (entity name + partner/company name). Opens
 * defaulted to the Invited cohort — the cohort that needs chasing.
 *
 * New component (no PO equivalent to reuse). Reads the program-wide endpoint
 * directly — no per-role scope split (Wave-2 decision #1). */
export function InsightsGrowthDrawer({ open, onClose }: InsightsGrowthDrawerProps) {
  const { accessToken } = useCurrentUser();
  const [status, setStatus] = React.useState<GrowthStatusFilter>("invited");
  const [typeFilter, setTypeFilter] = React.useState<GrowthTypeFilter>("all");
  const [period, setPeriod] = React.useState<Exclude<InsightsPeriod, "all">>("month");
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  // Re-seed the filters + period + search each time the drawer opens (Invited +
  // All types + This Month defaults).
  React.useEffect(() => {
    if (open) {
      setStatus("invited");
      setTypeFilter("all");
      setPeriod("month");
      setSearchInput("");
      setDebouncedSearch("");
    }
  }, [open]);

  React.useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const query = debouncedSearch.trim();
  const { data, isLoading, error } = useQuery<InsightsGrowthLedger>({
    queryKey: ["insights", "growth-ledger", status, period, query, accessToken],
    queryFn: () =>
      getInsightsGrowthLedger(accessToken!, {
        q: query === "" ? undefined : query,
        status: status === "all" ? undefined : status,
        period,
      }),
    enabled: !!accessToken && open,
  });

  const rows: InsightsGrowthLedgerRow[] = data?.rows ?? [];
  // CHP-848: type filter applies client-side before the sort. NOTE the
  // backend's status-vs-org rule: org rows are only ever 'registered', so
  // Invited/Pending views already show individuals only regardless of type.
  const typedRows = React.useMemo(
    () => (typeFilter === "all" ? rows : rows.filter((r) => r.type === typeFilter)),
    [rows, typeFilter],
  );
  const { sort, toggle, sortedRows } = useTableSort(typedRows, growthSortValue);

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full overflow-y-auto sm:w-[65vw] sm:max-w-[65vw]"
        data-testid="insights-growth-drawer"
      >
        <SheetHeader>
          {/* CHP-844: "Growth Pulse" → "Channel partner reach". */}
          <SheetTitle className="pr-8">Channel partner reach</SheetTitle>
          <SheetDescription>
            New partners and individuals across the program, and who is yet to register.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <StatCard
              title="New Partners (30d)"
              value={data?.new_orgs_30d ?? 0}
              tone="success"
              icon={Building2}
              testId="growth-new-orgs"
            />
            <StatCard
              title="New Individuals (30d)"
              value={data?.new_individuals_30d ?? 0}
              tone="success"
              icon={Users}
              testId="growth-new-individuals"
            />
            <StatCard
              title="Invited, Not Registered"
              value={data?.invited_not_registered_count ?? 0}
              tone="neutral"
              icon={MailQuestion}
              testId="growth-invited-not-registered"
            />
          </div>

          {/* CHP-848: filter bar aligned to the shared ledger style — type +
              status tab groups left; search with the Advanced Filters box
              (period) to its right. */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <Tabs
                value={typeFilter}
                onValueChange={(next) => setTypeFilter(next as GrowthTypeFilter)}
                data-testid="growth-type-filter"
              >
                <TabsList>
                  {TYPE_TABS.map((t) => (
                    <TabsTrigger
                      key={t.value}
                      value={t.value}
                      data-testid={`growth-type-${t.value}`}
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <Tabs
                value={status}
                onValueChange={(next) => setStatus(next as GrowthStatusFilter)}
                data-testid="growth-status-filter"
              >
                <TabsList>
                  {STATUS_TABS.map((t) => (
                    <TabsTrigger
                      key={t.value}
                      value={t.value}
                      data-testid={`growth-status-${t.value}`}
                    >
                      {t.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="flex w-full items-center gap-2 sm:w-auto">
              <div className="relative w-full sm:w-72">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  aria-hidden
                />
                <Input
                  type="search"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search name or partner…"
                  aria-label="Search growth ledger"
                  className="pl-9"
                  data-testid="growth-search"
                />
              </div>
              <GrowthAdvancedFilters period={period} onPeriodChange={setPeriod} />
            </div>
          </div>

          {isLoading ? (
            <p role="status" className="text-sm text-muted-foreground">
              Loading growth ledger…
            </p>
          ) : error ? (
            <FormError>Couldn&apos;t load the growth ledger.</FormError>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="entity" sort={sort} onToggle={toggle}>
                    Name
                  </SortableHeader>
                  {/* CHP-825: "Partner" → "Channel partner" across ledgers. */}
                  <SortableHeader column="partner" sort={sort} onToggle={toggle}>
                    Channel partner
                  </SortableHeader>
                  <SortableHeader column="type" sort={sort} onToggle={toggle}>
                    Type
                  </SortableHeader>
                  <SortableHeader column="invite" sort={sort} onToggle={toggle}>
                    Invite Date
                  </SortableHeader>
                  <SortableHeader column="status" sort={sort} onToggle={toggle}>
                    Status
                  </SortableHeader>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map((r) => (
                  <TableRow
                    key={`${r.type}-${r.entity_id}`}
                    data-testid={`growth-row-${r.entity_id}`}
                  >
                    <TableCell className="font-medium">
                      {r.entity_name}
                      {r.is_new_30d ? (
                        <Badge variant="secondary" className="ml-2">
                          New
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{r.partner_name ?? "—"}</TableCell>
                    <TableCell className="capitalize">
                      {r.type === "org" ? "Organisation" : "Individual"}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatDate(r.invite_date ?? null)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {r.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground">
                      No records found.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
